/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from "react";

export const dash = "—";
export const num = (v: unknown, dp?: number) =>
  v == null || v === "" || Number.isNaN(Number(v)) ? dash : dp != null ? Number(v).toFixed(dp) : String(Math.round(Number(v) * 100) / 100);

export const Card = ({ title, subtitle, action, children, wide }: { title: string; subtitle?: string; action?: ReactNode; children: ReactNode; wide?: boolean }) => (
  <section className={`pt-card${wide ? " pt-card--wide" : ""}`}>
    <header className="pt-card-head">
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action}
    </header>
    {children}
  </section>
);

export const StatGrid = ({ items, cols = 4 }: { items: { label: string; value: string }[]; cols?: number }) => (
  <div className="pt-stat-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
    {items.map((i) => (
      <div className="pt-stat" key={i.label}>
        <strong>{i.value}</strong>
        <span>{i.label}</span>
      </div>
    ))}
  </div>
);

export const Chips = ({ items }: { items: { label: string; value: string }[] }) => (
  <div className="pt-chips">
    {items.map((i) => (
      <span className="pt-chip" key={i.label}>
        {i.label} <b>{i.value}</b>
      </span>
    ))}
  </div>
);

export const BarRow = ({ title, right, value, max, barLabel, chips }: { title: string; right?: string; value: number; max: number; barLabel: string; chips?: { label: string; value: string }[] }) => (
  <div className="pt-bar-row">
    <div className="pt-bar-top">
      <span className="pt-bar-title">{title}</span>
      <span className="pt-bar-value">{barLabel}{right && <em className="pt-muted"> · {right}</em>}</span>
    </div>
    <div className="pt-bar" role="presentation">
      <div className="pt-bar-fill" style={{ width: `${max > 0 && value > 0 ? Math.max(3, (value / max) * 100) : 0}%` }} />
    </div>
    {chips && <Chips items={chips} />}
  </div>
);

const FORMAT_LABELS: Record<string, string> = { TEST_MATCH: "Multi Day", THE_HUNDRED: "The Hundred", LIMITED_OVERS: "Limited Overs", T20: "T20", T10: "T10", INDOOR: "Indoor" };
export const formatLabel = (f?: string | null) => (f ? FORMAT_LABELS[f] ?? f.replace(/_/g, " ") : "—");

export const Segmented = <T extends string>({ options, value, onChange }: { options: { key: T; label: string }[]; value: T; onChange: (k: T) => void }) => (
  <div className="pt-segmented" role="tablist">
    {options.map((o) => (
      <button key={o.key} type="button" role="tab" aria-selected={value === o.key} className={value === o.key ? "active" : ""} onClick={() => onChange(o.key)}>
        {o.label}
      </button>
    ))}
  </div>
);

export const EmptyState = ({ title, hint }: { title: string; hint: string }) => (
  <div className="pt-empty">
    <strong>{title}</strong>
    <p>{hint}</p>
  </div>
);

export const SimpleTable = ({ cols, rows }: { cols: string[]; rows: string[][] }) => (
  <div className="pt-table-scroll">
    <table className="pt-table">
      <thead>
        <tr>{cols.map((c, i) => <th key={c} className={i === 0 ? "pt-first" : ""}>{c}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri}>{r.map((c, i) => <td key={i} className={i === 0 ? "pt-first" : ""}>{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const QueryState = ({ loading, error, onRetry, children }: { loading: boolean; error: boolean; onRetry: () => void; children: ReactNode }) => {
  if (loading) return <div className="pt-loading">Loading…</div>;
  if (error)
    return (
      <div className="pt-empty">
        <strong>Something went wrong</strong>
        <button type="button" className="pt-retry" onClick={onRetry}>Retry</button>
      </div>
    );
  return <>{children}</>;
};
